import { h } from "preact";
import { useState, useEffect } from "preact/hooks";
import { Card, Select, Alert, Typography, Modal, Spin } from 'antd';
import axios from "axios";

const { Option } = Select;
const { Text } = Typography;

const ConsumerSelector = ({
                              selectedConsumer,
                              onConsumerSelect,
                              hasChanges,
                              loading
                          }) => {
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    const searchConsumers = async (searchText) => {
        if (!searchText || searchText.length < 2) {
            setSearchResults([]);
            return;
        }

        try {
            setSearching(true);
            const response = await axios.get("/admin/consumers", {
                params: {
                    username: searchText,
                    limit: 20,
                    offset: 0
                }
            });
            setSearchResults(response.data || []);
        } catch (error) {
            console.error("Error searching consumers:", error);
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            searchConsumers(searchValue);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchValue]);

    const handleSearch = (value) => {
        setSearchValue(value);
    };

    const handleConsumerSelect = (value, option) => {
        if (hasChanges) {
            Modal.confirm({
                title: 'Unsaved Changes',
                content: 'You have unsaved changes. Are you sure you want to switch consumers?',
                onOk: () => {
                    const consumer = searchResults.find(c => c.consumer_id === value);
                    onConsumerSelect(consumer);
                }
            });
        } else {
            const consumer = searchResults.find(c => c.consumer_id === value);
            onConsumerSelect(consumer);
        }
    };

    const handleClear = () => {
        setSearchValue('');
        setSearchResults([]);
    };

    // If there's a selected consumer but it's not in search results, add it
    const displayOptions = [...searchResults];
    if (selectedConsumer && !searchResults.find(c => c.consumer_id === selectedConsumer.consumer_id)) {
        displayOptions.unshift(selectedConsumer);
    }

    return (
        <Card title="Consumer Selection" style={{ marginBottom: '24px' }}>
            <div style={{ marginBottom: '16px' }}>
                <Text strong>Search and Select Consumer to Configure</Text>
                <Select
                    style={{ width: '100%', marginTop: '8px' }}
                    placeholder="Type to search consumers (min 2 characters)..."
                    showSearch
                    value={selectedConsumer?.consumer_id}
                    onSearch={handleSearch}
                    onChange={handleConsumerSelect}
                    onClear={handleClear}
                    allowClear
                    size="large"
                    loading={searching}
                    filterOption={false}
                    notFoundContent={
                        searching ? (
                            <div style={{ textAlign: 'center', padding: '8px' }}>
                                <Spin size="small" /> Searching...
                            </div>
                        ) : searchValue.length > 0 && searchValue.length < 2 ? (
                            <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                                Type at least 2 characters to search
                            </div>
                        ) : searchValue.length >= 2 && searchResults.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                                No consumers found
                            </div>
                        ) : null
                    }
                >
                    {displayOptions.map(consumer => (
                        <Option key={consumer.consumer_id} value={consumer.consumer_id}>
                            <div>
                                <div style={{ fontWeight: 'bold' }}>{consumer.username}</div>
                                <div style={{ fontSize: '12px', color: '#666' }}>{consumer.company}</div>
                                <div style={{ fontSize: '11px', color: '#999' }}>{consumer.consumer_id}</div>
                            </div>
                        </Option>
                    ))}
                </Select>

                {searchValue.length > 0 && searchValue.length < 2 && (
                    <Text type="secondary" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Continue typing to search consumers...
                    </Text>
                )}
            </div>

            {selectedConsumer && (
                <Alert
                    message="Selected Consumer"
                    description={
                        <div>
                            <Text strong>{selectedConsumer.username}</Text> - {selectedConsumer.company}
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                ID: {selectedConsumer.consumer_id}
                            </Text>
                        </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginTop: '16px' }}
                    icon={loading.loadingSettings ? <Spin size="small" /> : undefined}
                />
            )}
        </Card>
    );
};

export default ConsumerSelector;